/*
| Builders, as data.
|
| A builder is an application, or a display group showing several as one. The
| API applies the display rules: hidden builders are left out of every list
| and answer 404, so whatever arrives here is shown.
|
| Sources (all metered public reads):
|   /analytics/builders                     the directory
|   /analytics/builders/{b}                 one builder (uuid, code or client id)
|   /analytics/builders/{b}/totals|series   one builder over a window
|   /analytics/builder-leaderboard          ranked over a window, up to 100
|   /trades?takerApplicationId=             one application's taker trades
|
| Exact traders, deposits/withdrawals/net flow and the default flag come with
| every response; per-trade builder fees (TradeData.builderFeeRaw) are
| optional until 0XM-3663's last part ships. Anything absent renders as "—".
*/

import { marketHref, pairsById } from '../lib/catalog.js';
import { metric } from './analytics.js';
import { scaleRaw, toMs, toNumber } from '../lib/values.js';

const has = (object, key) => object !== null && object !== undefined && object[key] !== undefined;

function addOnRange(values) {
  const bps = values.filter((v) => Number.isFinite(v));
  if (!bps.length) return null;
  const low = Math.min(...bps);
  const high = Math.max(...bps);
  return low === high ? `${low} bps` : `${low}–${high} bps`;
}

const CODE = /^[A-Za-z0-9_-]{1,64}$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** #rrggbb from #rrggbb or #rrggbbaa; charts append their own alpha. */
function rgb(color) {
  const match = /^#([0-9a-f]{6})([0-9a-f]{2})?$/i.exec(String(color ?? ''));
  return match ? `#${match[1]}` : null;
}

/** A builder's page: by code when it is a plain slug, else by uuid, else no link at all. */
function builderHref(code, id) {
  const key = code && CODE.test(code) ? code : (id && UUID.test(id) ? id : null);
  return key ? `/builders/${encodeURIComponent(key)}` : null;
}

export function builderRef(builder) {
  const applications = builder?.applications ?? [];
  const href = builderHref(builder.code, builder.id);
  const key = href ? decodeURIComponent(href.slice('/builders/'.length)) : null;
  const defaults = applications.filter((app) => has(app, 'isDefault'));

  return {
    id: builder.id,
    key,
    href,
    kind: builder.kind,
    name: builder.name,
    code: builder.code ?? null,
    color: rgb(builder.color),
    verified: builder.verified === true,
    active: applications.some((app) => app.active),
    isDefault: defaults.length ? defaults.some((app) => app.isDefault === true) : null,
    clientIds: applications.map((app) => app.clientId),
    applications,
    takerAddOn: addOnRange(applications.map((app) => Number(app.additionalTakerFeeBps))),
    makerAddOn: addOnRange(applications.map((app) => Number(app.additionalMakerFeeBps))),
    groupRef: builder.group
      ? { name: builder.group.name, href: builderHref(builder.group.code, builder.group.id) }
      : null,
  };
}

const bps = (fees, volume) => {
  const f = toNumber(fees);
  const v = toNumber(volume);
  return f === null || !v ? null : (f / v) * 10_000;
};

export function leaderboardRows(rows) {
  const total = (rows ?? []).reduce((sum, row) => sum + (toNumber(row.volumeUsd) ?? 0), 0);

  return (rows ?? []).map((row) => ({
    ref: builderRef(row.builder),
    volumeUsd: row.volumeUsd ?? null,
    sharePercent: total > 0 ? ((toNumber(row.volumeUsd) ?? 0) / total) * 100 : 0,
    feesUsd: row.feesUsd ?? null,
    makerFeesUsd: row.makerFeesUsd ?? null,
    avgFeeBps: bps(row.feesUsd, row.volumeUsd),
    newTraders: row.newTraders ?? null,
    tradesCount: row.tradesCount ?? null,
    traders: row.activeTakers ?? null,
    depositsUsd: row.depositsUsd ?? null,
    withdrawalsUsd: row.withdrawalsUsd ?? null,
    netFlowUsd: row.netFlowUsd ?? null,
  }));
}

const sum = (rows, key) => (rows ?? []).reduce((total, row) => total + (toNumber(row[key]) ?? 0), 0);

// A sum is a total only when every row sent the field; a partial sum is unmeasured.
const sends = (list, key) => Array.isArray(list) && list.length > 0 && list.every((row) => row[key] !== undefined && row[key] !== null);
const flowMetric = (rows, prev, key) => metric(sends(rows, key) ? sum(rows, key) : null, sends(prev, key) ? sum(prev, key) : null);

/** Board totals with deltas; a board that failed to load is unmeasured, not zero. */
export function leaderboardKpis(rows, previousRows) {
  // A board at its row cap may have left builders off, so its sums are not totals.
  const usable = (list) => (list && list.length < BOARD_LIMIT ? list : null);
  const current = usable(rows);
  const prev = usable(previousRows ?? null);
  rows = current;
  const pick = (key) => metric(rows ? sum(rows, key) : null, prev ? sum(prev, key) : null);

  return {
    complete: current !== null,
    volume: pick('volumeUsd'),
    fees: pick('feesUsd'),
    makerFees: pick('makerFeesUsd'),
    trades: pick('tradesCount'),
    newTraders: pick('newTraders'),
    avgFeeBps: metric(rows ? bps(sum(rows, 'feesUsd'), sum(rows, 'volumeUsd')) : null, prev ? bps(sum(prev, 'feesUsd'), sum(prev, 'volumeUsd')) : null),
    // Flows exist only where the board sends them.
    deposits: flowMetric(rows, prev, 'depositsUsd'),
    withdrawals: flowMetric(rows, prev, 'withdrawalsUsd'),
    netFlow: flowMetric(rows, prev, 'netFlowUsd'),
  };
}

function byBucket(series) {
  return new Map((series?.points ?? []).map((point) => [toMs(point.bucketStart), toNumber(point.volumeUsd)]));
}

/**
 * The routed-volume chart: one band per top builder, plus "Others", which is
 * the protocol series less those bands. Both count displayed builders only,
 * so the difference is everyone else on the board.
 */
export function stackedVolume(topRefs, seriesById, protocolSeries) {
  const protocol = protocolSeries ? byBucket(protocolSeries) : null;
  // Without the protocol series, every builder series that loaded names the buckets.
  const labels = protocol
    ? [...protocol.keys()]
    : [...new Set(topRefs.flatMap((ref) => [...byBucket(seriesById.get(ref.id)).keys()]))].sort((a, b) => a - b);

  const bands = topRefs.map((ref) => {
    const values = byBucket(seriesById.get(ref.id));
    return { id: ref.id, name: ref.name, color: ref.color, values: labels.map((at) => values.get(at) ?? null) };
  });

  if (!protocol) return { labels, series: bands };

  // Others is the remainder, so it is unmeasured wherever a top builder's
  // point is: an unknown band must not be relabelled as everyone else's volume.
  const others = labels.map((at, i) => {
    const total = protocol.get(at);
    if (total === null || total === undefined) return null;
    if (bands.some((band) => band.values[i] === null || band.values[i] === undefined)) return null;
    const top = bands.reduce((acc, band) => acc + band.values[i], 0);
    return Math.max(0, total - top);
  });

  return { labels, series: [...bands, { id: 'others', name: 'Others', color: null, values: others }] };
}

/** The board's row cap: a full board may have left builders off. */
export const BOARD_LIMIT = 100;

/**
 * Every displayed builder with its all-time figures from the board. Absence
 * means "routed nothing" only when the board loaded and was not capped;
 * otherwise an absent builder's figures are unmeasured.
 */
export function directoryRows(builders, allTimeRows) {
  const byId = new Map((allTimeRows ?? []).map((row) => [row.builder?.id, row]));
  const complete = Array.isArray(allTimeRows) && allTimeRows.length < BOARD_LIMIT;
  const absent = (zero) => (complete ? zero : null);

  return (builders ?? []).map((builder) => {
    const row = byId.get(builder.id) ?? null;
    const exact = has(row, 'activeTakers') && row.activeTakers !== null;
    return {
      ref: builderRef(builder),
      volumeUsd: row ? (row.volumeUsd ?? null) : absent('0'),
      feesUsd: row ? (row.feesUsd ?? null) : absent('0'),
      tradesCount: row ? (row.tradesCount ?? null) : absent(0),
      traders: exact ? row.activeTakers : row ? (row.newTraders ?? null) : absent(0),
      tradersExact: exact,
      depositsUsd: row?.depositsUsd ?? null,
      withdrawalsUsd: row?.withdrawalsUsd ?? null,
      netFlowUsd: row?.netFlowUsd ?? null,
    };
  });
}

export function builderDetail({ builder, day, allTime }) {
  return {
    ref: builderRef(builder),
    volume24hUsd: day?.volumeUsd ?? null,
    fees24hUsd: day?.feesUsd ?? null,
    makerFees24hUsd: day?.makerFeesUsd ?? null,
    trades24h: day?.tradesCount ?? null,
    volumeTotalUsd: allTime?.volumeUsd ?? null,
    feesTotalUsd: allTime?.feesUsd ?? null,
    makerFeesTotalUsd: allTime?.makerFeesUsd ?? null,
    tradesTotal: allTime?.tradesCount ?? null,
    traders: allTime?.activeTakers ?? null,
    depositsUsd: allTime?.depositsUsd ?? null,
    withdrawalsUsd: allTime?.withdrawalsUsd ?? null,
    netFlowUsd: allTime?.netFlowUsd ?? null,
  };
}

export function builderTradeRows(trades, pairs) {
  const byId = pairsById(pairs);

  return (trades ?? []).map((trade) => {
    const data = trade.data ?? {};
    const pair = byId.get(trade.tradingPairId) ?? null;
    return {
      id: data.tradeId,
      symbol: pair?.symbol ?? null,
      marketHref: pair ? marketHref(pairs, pair) : null,
      pairId: trade.tradingPairId ?? null,
      mode: trade.tradingMode ?? pair?.marketType ?? null,
      side: data.makerSide === 'SELL' ? 'BUY' : 'SELL',
      price: data.price ?? null,
      valueUsd: pair ? scaleRaw(data.quoteVolumeRaw, pair.quoteDecimals) : null,
      builderFeeUsd: pair ? scaleRaw(data.builderFeeRaw, pair.quoteDecimals) : null,
      executedAt: toMs(data.executedAt),
    };
  });
}

/** An execution time (ISO, or epoch ms) in microseconds since the epoch, as a BigInt; -1 when unreadable. */
function executedMicros(iso) {
  if (/^\d+$/.test(String(iso ?? ''))) return BigInt(iso) * 1000n; // epoch milliseconds
  const match = /^(.*T\d{2}:\d{2}:\d{2})(?:\.(\d+))?(Z|[+-]\d{2}:?\d{2})$/.exec(String(iso ?? ''));
  if (!match) return -1n;
  const seconds = Date.parse(`${match[1]}${match[3]}`);
  if (Number.isNaN(seconds)) return -1n;
  const fraction = BigInt((match[2] ?? '').padEnd(6, '0').slice(0, 6));
  return BigInt(seconds) * 1000n + fraction;
}

/**
 * Several applications' feeds as one list: each trade once, newest first. The
 * order uses the full execution time to the microsecond and the feed's
 * descending trade-id tie-breaker, before the limit and before the display
 * rounds the time to milliseconds.
 */
export function mergeBuilderTrades(feeds, pairs, limit = 20) {
  const seen = new Set();
  const unique = (feeds ?? []).flat().filter((trade) => {
    const id = trade?.data?.tradeId;
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
  const at = (trade) => executedMicros(trade.data.executedAt);
  const byCodeUnit = (x, y) => (x < y ? -1 : x > y ? 1 : 0);
  unique.sort((a, b) => byCodeUnit(at(b), at(a)) || byCodeUnit(String(b.data.tradeId), String(a.data.tradeId)));
  return builderTradeRows(unique.slice(0, limit), pairs);
}

/** Net flow per bucket over the visible builders; a gap wherever any of their points is missing. */
export function netFlowPoints(labels, refs, seriesById) {
  const maps = refs.map((ref) => new Map((seriesById.get(ref.id)?.points ?? []).map((p) => [toMs(p.bucketStart), toNumber(p.netFlowUsd)])));
  return labels.map((at) => {
    let total = 0;
    for (const values of maps) {
      const value = values.get(at);
      if (value === null || value === undefined) return null;
      total += value;
    }
    return total;
  });
}
